import type { TrafficSourcesResponse } from '@qlk/shared';

import { EmptyState, Skeleton } from '../../../ui/index.js';
import { gonSo, phanTram } from './dinhDang.js';

type DongNguon = TrafficSourcesResponse['items'][number];

/**
 * Chỉ những nguồn **có lượt xem**, sắp giảm dần.
 *
 * Không gộp "Khác" nữa (bản 30/10/2026 có, bản này bỏ): một dòng "Khác 8,4%"
 * không trả lời được câu hỏi nào — người đọc không biết nó gồm gì, và cũng
 * không bấm vào đâu để biết. Thà bày đủ những nguồn thật có lượt xem, vì sau
 * khi lọc hết dòng 0 thì danh sách còn ngắn.
 *
 * Hai luật còn lại:
 *
 * 1. **0 lượt xem thì không hiện.** YouTube trả đủ mười mấy mã nguồn, phần lớn
 *    là dòng 0; mười dòng số 0 làm trôi mất mấy dòng có nghĩa. Lọc theo **lượt
 *    xem**, không theo phần trăm: nguồn có 3 lượt trên 2 triệu làm tròn ra
 *    `0,0%` nhưng vẫn là một nguồn thật.
 * 2. Danh sách vào đã được máy chủ sắp theo lượt xem giảm dần
 *    (`analytics.getTrafficSourceBreakdown`), nên ở đây chỉ lọc chứ không sắp
 *    lại — trừ khi cần, để bảo đảm thứ tự không phụ thuộc thứ tự đáp án.
 */
export function gopNguonLuuLuong(items: DongNguon[]): DongNguon[] {
  return items.filter((d) => d.views > 0).sort((a, b) => b.views - a.views);
}

/**
 * "Lượt xem đến từ đâu" — thanh ngang CSS thuần kèm phần trăm.
 *
 * Nhãn của từng nguồn do máy chủ dựng bằng `shared/nguonLuuLuong`, không dịch
 * lại ở đây: một mã nguồn phải ra cùng một chữ ở mọi màn hình.
 */
export function NguonLuuLuong({
  duLieu,
  dangTai,
}: {
  duLieu: TrafficSourcesResponse | undefined;
  dangTai: boolean;
}) {
  const items = gopNguonLuuLuong(duLieu?.items ?? []);

  return (
    <section className="rounded-the border border-vien bg-be-mat p-6">
      <h2 className="text-tieu-de-khoi font-semibold text-chu">Lượt xem đến từ đâu</h2>
      <p className="mt-1 text-phu text-chu-mo">
        Chỉ hiện nguồn có lượt xem · tổng {gonSo(duLieu?.totalViews)}
      </p>

      {dangTai ? (
        <ul className="mt-4 flex flex-col gap-5" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={`khung-${String(i)}`} cao="2rem" />
          ))}
        </ul>
      ) : items.length === 0 ? (
        <EmptyState title="Chưa có dữ liệu nguồn lưu lượng" />
      ) : (
        <ul className="mt-4 flex flex-col gap-4">
          {items.map((d) => (
            <li key={d.sourceType}>
              <div className="flex items-baseline justify-between gap-3 text-co-ban">
                <span className="truncate text-chu">{d.label}</span>

                {/* Số lượt **và** phần trăm: phần trăm nói tỷ trọng, số lượt nói
                    quy mô — `2,4%` của 2 triệu khác hẳn `2,4%` của 20 nghìn.
                    Một chữ số thập phân vì `51%` không phân biệt được hai nguồn
                    sát nhau. */}
                <span className="shrink-0 whitespace-nowrap">
                  <span className="so-lieu text-chu-mo">{gonSo(d.views)}</span>
                  <span className="so-lieu ml-2 font-semibold text-chu">{phanTram(d.percent)}</span>
                </span>
              </div>
              <div
                className="mt-2 h-2 w-full overflow-hidden rounded-full bg-neutral-nen"
                role="img"
                aria-label={`${d.label}: ${phanTram(d.percent)} (${gonSo(d.views)} lượt xem)`}
              >
                <span
                  className="block h-full rounded-full bg-nhan"
                  style={{ width: `${String(Math.max(d.percent, 1))}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
